import { Link } from 'react-router-dom';
import { 
  Check, 
  Building2, 
  Shield, 
  Crown,
  Leaf,
  BarChart3,
  Clock,
  FileCheck,
  Users,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

const PricingTier = ({ tier, price, period, description, features, badge, badgeColor, highlighted, ctaText, ctaLink, externalLink }) => (
  <Card className={`relative ${highlighted ? 'border-primary shadow-lg scale-105' : 'border-border'}`}>
    {highlighted && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
      </div>
    )}
    <CardHeader className="text-center pb-4">
      {badge && (
        <Badge variant="outline" className={`mb-2 mx-auto ${badgeColor}`}>
          {badge}
        </Badge>
      )}
      <CardTitle className="text-xl">{tier}</CardTitle>
      <CardDescription className="text-sm">{description}</CardDescription>
      <div className="mt-4">
        <span className="text-3xl font-bold">{price}</span>
        {period && <span className="text-muted-foreground ml-1">{period}</span>}
      </div>
    </CardHeader>
    <CardContent>
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {externalLink ? (
        <Button className="w-full" variant={highlighted ? 'default' : 'outline'} asChild>
          <a href={ctaLink} target="_blank" rel="noopener noreferrer">
            {ctaText || 'Get Started'}
          </a>
        </Button>
      ) : (
        <Button className="w-full" variant={highlighted ? 'default' : 'outline'} asChild>
          <Link to={ctaLink || '/suggest?type=vendor'}>
            {ctaText || 'Get Started'}
          </Link>
        </Button>
      )}
    </CardContent>
  </Card>
);

export default function VendorPricingPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Vendor Access & Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
              GoCompareAI offers tiered vendor access designed to support accuracy, transparency, 
              and responsible representation of AI platforms.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 max-w-2xl mx-auto">
              <p className="text-sm text-muted-foreground">
                <strong>Vendor access is optional</strong> and does not influence rankings, comparisons, or recommendations.
                <br />
                <span className="text-primary font-medium">All users see the same independent platform outcomes.</span>
              </p>
            </div>
          </div>

          {/* Pricing Tiers */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            
            {/* Claimed - Free */}
            <PricingTier
              tier="Claimed"
              price="£0"
              period="/ year"
              description="For vendors who want basic accuracy without ongoing management."
              badge="Free"
              badgeColor="bg-gray-100 text-gray-700"
              features={[
                "Claim a single platform profile",
                "Edit core factual fields (summary, description, features, links)",
                "Admin-reviewed updates",
                "Neutral listing (no public badge)"
              ]}
              ctaText="Claim Your Platform"
              ctaLink="/suggest?type=vendor"
            />

            {/* Verified Partner */}
            <PricingTier
              tier="Verified Partner"
              price="From £1,200"
              period="/ year"
              description="For vendors who want to demonstrate credibility and engagement."
              badge="Verified Partner"
              badgeColor="bg-blue-100 text-blue-700"
              highlighted={true}
              features={[
                "Verified Partner badge on platform profile",
                "Extended profile editing (pricing, use cases, tags)",
                "Engagement analytics (profile views, comparison inclusion)",
                "Multi-platform management",
                "Priority review of updates"
              ]}
              ctaText="Become Verified - Pay Now"
              ctaLink="https://monzo.com/pay/r/marcus-james-day_9LqsCH19y2fxYp"
              externalLink={true}
            />

            {/* Enterprise Partner */}
            <PricingTier
              tier="Enterprise Partner"
              price="£5,000"
              period="/ year"
              description="For established AI providers selling into regulated, enterprise, or public-sector environments."
              badge="Enterprise Partner"
              badgeColor="bg-amber-100 text-amber-700"
              features={[
                "Enterprise Partner badge",
                "Fast-lane publishing for approved fields",
                "Full analytics suite",
                "Comprehensive profile controls",
                "Sustainability disclosure included",
                "Priority support",
                "Multi-platform management"
              ]}
              ctaText="Enterprise Partner - Pay Now"
              ctaLink="https://monzo.com/pay/r/marcus-james-day_PFzuXguRgQPWD7"
              externalLink={true}
            />
          </div>

          {/* Sustainability Add-on */}
          <div className="mb-12">
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Sustainability & Environmental Disclosure</CardTitle>
                    <CardDescription>Optional Add-On for Verified Partners</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Available to Verified Partners who wish to disclose environmental commitments and certifications.
                    </p>
                    <ul className="space-y-2">
                      {[
                        "Public sustainability disclosure section",
                        "Upload certifications and supporting evidence",
                        "Clear vendor-provided labelling",
                        "Admin review prior to publishing"
                      ].map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-col justify-center items-center md:items-end">
                    <div className="text-center md:text-right">
                      <p className="text-sm text-muted-foreground mb-1">Annual add-on</p>
                      <p className="text-2xl font-bold">From £600<span className="text-base font-normal text-muted-foreground"> / year</span></p>
                    </div>
                    <Button variant="outline" className="mt-4 border-green-300 text-green-700 hover:bg-green-50" asChild>
                      <a href="https://monzo.com/pay/r/marcus-james-day_6ELHQnq0HSljc3" target="_blank" rel="noopener noreferrer">
                        Add Sustainability - Pay Now
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Feature Comparison Table */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-center mb-6">Feature Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Feature</th>
                    <th className="text-center py-3 px-4 font-medium">Claimed</th>
                    <th className="text-center py-3 px-4 font-medium bg-primary/5">Verified</th>
                    <th className="text-center py-3 px-4 font-medium">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { feature: "Platform profile claim", claimed: true, verified: true, enterprise: true },
                    { feature: "Edit core fields", claimed: true, verified: true, enterprise: true },
                    { feature: "Extended profile editing", claimed: false, verified: true, enterprise: true },
                    { feature: "Data handling fields", claimed: false, verified: false, enterprise: true },
                    { feature: "Public trust badge", claimed: false, verified: "Blue", enterprise: "Gold" },
                    { feature: "Daily edit limit", claimed: "3", verified: "10", enterprise: "50" },
                    { feature: "Auto-publish (links)", claimed: false, verified: true, enterprise: true },
                    { feature: "Auto-publish (content)", claimed: false, verified: false, enterprise: true },
                    { feature: "Engagement analytics", claimed: false, verified: true, enterprise: true },
                    { feature: "Comparison analytics", claimed: false, verified: false, enterprise: true },
                    { feature: "Multi-platform management", claimed: false, verified: true, enterprise: true },
                    { feature: "Sustainability disclosure", claimed: false, verified: "Add-on", enterprise: true },
                    { feature: "Priority support", claimed: false, verified: false, enterprise: true },
                  ].map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-3 px-4">{row.feature}</td>
                      <td className="text-center py-3 px-4">
                        {row.claimed === true ? <Check className="w-4 h-4 text-primary mx-auto" /> : 
                         row.claimed === false ? <span className="text-muted-foreground">—</span> : 
                         <span className="text-xs">{row.claimed}</span>}
                      </td>
                      <td className="text-center py-3 px-4 bg-primary/5">
                        {row.verified === true ? <Check className="w-4 h-4 text-primary mx-auto" /> : 
                         row.verified === false ? <span className="text-muted-foreground">—</span> : 
                         <span className="text-xs">{row.verified}</span>}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.enterprise === true ? <Check className="w-4 h-4 text-primary mx-auto" /> : 
                         row.enterprise === false ? <span className="text-muted-foreground">—</span> : 
                         <span className="text-xs">{row.enterprise}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Independence Statement */}
          <div className="bg-muted/30 rounded-lg p-6 text-center">
            <h3 className="font-semibold text-foreground mb-2">Independence Statement</h3>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              GoCompareAI does not sell preferential placement, rankings, or recommendations.
              <br />
              Vendor access exists to support transparency and factual accuracy only.
            </p>
          </div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
}
